import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function AdminPaymentMethods() {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameUrdu: '',
    isActive: true,
    order: 0
  });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin/login");
        return;
      }

      const response = await api.get("/payment-method/admin/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentMethods(response.data.paymentMethods || []);
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      toast.error("Payment methods لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("adminToken");
      const payload = {
        name: formData.name,
        nameUrdu: formData.nameUrdu,
        isActive: formData.isActive,
        order: formData.order
      };

      if (editingMethod) {
        await api.put(`/payment-method/admin/${editingMethod._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Payment method اپڈیٹ ہو گیا");
      } else {
        await api.post("/payment-method/admin/create", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Payment method شامل ہو گیا");
      }

      setShowAddModal(false);
      setEditingMethod(null);
      resetForm();
      fetchPaymentMethods();
    } catch (error) {
      console.error("Save payment method error:", error);
      toast.error("Payment method محفوظ کرنے میں ناکام");
    }
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setFormData({
      name: method.name || '',
      nameUrdu: method.nameUrdu || '',
      isActive: method.isActive !== undefined ? method.isActive : true,
      order: method.order || 0
    });
    setShowAddModal(true);
  };

  const handleDelete = async (methodId) => {
    if (!window.confirm('کیا آپ واقعی یہ payment method حذف کرنا چاہتے ہیں؟')) {
      return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      await api.delete(`/payment-method/admin/${methodId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment method حذف ہو گیا");
      fetchPaymentMethods();
    } catch (error) {
      console.error("Delete payment method error:", error);
      toast.error("Payment method حذف کرنے میں ناکام");
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nameUrdu: '',
      isActive: true,
      order: 0
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
          <p className="mt-4 text-gray-600">لوڈ ہو رہا ہے...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Payment Methods Management - Admin</title>
      </Helmet>
      <div className="min-h-screen bg-brand-background py-8 px-4" dir="rtl">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-brand-card animate-fade-in-up">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-brand-header mb-2 font-urdu">
                  ادائیگی کے طریقے
                </h1>
                <p className="text-gray-600 font-urdu">
                  Recovery form کے لیے payment methods کو منظم کریں
                </p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setEditingMethod(null);
                  setShowAddModal(true);
                }}
                className="px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="font-urdu">نیا طریقہ شامل کریں</span>
              </button>
            </div>
          </div>

          {/* Payment Methods List */}
          <div className="bg-white rounded-lg shadow-lg border border-brand-card animate-fade-in-up">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-background">
                  <tr>
                    <th className="px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card font-urdu">
                      ترتیب
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card font-urdu">
                      نام (انگریزی)
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card font-urdu">
                      نام (اردو)
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-brand-header border-b border-brand-card font-urdu">
                      حالت
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-brand-header border-b border-brand-card font-urdu">
                      عمل
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paymentMethods.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500 font-urdu">
                        ابھی کوئی payment method نہیں
                      </td>
                    </tr>
                  ) : (
                    paymentMethods.map((method, index) => (
                      <tr key={method._id} className={`border-b border-brand-card hover:bg-brand-background transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-4 py-3 text-right">
                          {method.order || 0}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {method.name}
                        </td>
                        <td className="px-4 py-3 text-right font-urdu">
                          {method.nameUrdu || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            method.isActive 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {method.isActive ? 'فعال' : 'غیر فعال'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEdit(method)}
                              className="px-3 py-1 bg-brand-button hover:bg-yellow-400 text-gray-900 text-sm rounded transition-all transform hover:scale-105"
                            >
                              تبدیل
                            </button>
                            <button
                              onClick={() => handleDelete(method._id)}
                              className="px-3 py-1 bg-brand-alert hover:bg-red-600 text-white text-sm rounded transition-all transform hover:scale-105"
                            >
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-brand-card">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-brand-header font-urdu">
                    {editingMethod ? 'طریقہ تبدیل کریں' : 'نیا طریقہ شامل کریں'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingMethod(null);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 font-urdu">
                      نام (انگریزی) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      placeholder="e.g., Easypaisa, Punjab Bank"
                    />
                  </div>

                  <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 font-urdu">
                      نام (اردو)
                    </label>
                    <input
                      type="text"
                      name="nameUrdu"
                      value={formData.nameUrdu}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all font-urdu"
                      style={{ fontFamily: "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" }}
                      placeholder="مثال: ایزی پیسہ، پنجاب بینک"
                    />
                  </div>

                  <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 font-urdu">
                      ترتیب (Order)
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1 font-urdu">
                      کم نمبر والے طریقے پہلے دکھائیں گے
                    </p>
                  </div>

                  <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-brand-header border-brand-card rounded focus:ring-brand-header"
                      />
                      <span className="text-sm font-medium text-gray-700 font-urdu">
                        فعال (Active)
                      </span>
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setEditingMethod(null);
                        resetForm();
                      }}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-all transform hover:scale-105"
                    >
                      <span className="font-urdu">منسوخ</span>
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-lg"
                    >
                      <span className="font-urdu">{editingMethod ? 'اپڈیٹ کریں' : 'محفوظ کریں'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminPaymentMethods;

